/**
 * The sidebar's fixed menu: which items it shows and in what order. Stored as
 * the synced setting `sidebar_menu`, so every device shows the same menu.
 * Items added later (new modules, plugin views) appear at the end until moved.
 */
import { create } from 'zustand';
import { useStore, useStoreQuery } from '../db/context';

export const SIDEBAR_MENU = 'sidebar_menu';

/** Built-in items, in their default order. Plugin views are `plugin:<page>:<view>`. */
export const BUILT_IN_ITEMS = [
  'dashboard',
  'ai',
  'tasks',
  'projects',
  'memos',
  'graph',
  'templates',
  'plugins',
  'tips',
] as const;

export interface MenuEntry {
  id: string;
  hidden: boolean;
}

/** The stored menu completed with items it does not know yet (in their default place). */
export function resolveMenu(raw: string | null | undefined, available: string[]): MenuEntry[] {
  let stored: MenuEntry[] = [];
  try {
    const data: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(data))
      stored = data.filter(
        (e): e is MenuEntry =>
          !!e &&
          typeof (e as MenuEntry).id === 'string' &&
          typeof (e as MenuEntry).hidden === 'boolean',
      );
  } catch {
    stored = [];
  }
  const known = stored.filter((e) => available.includes(e.id));
  const missing = available
    .filter((id) => !known.some((e) => e.id === id))
    .map((id) => ({ id, hidden: false }));
  return [...known, ...missing];
}

export function useSidebarMenu(available: string[]): MenuEntry[] {
  const raw = useStoreQuery((s) => s.getSetting(SIDEBAR_MENU), [], ['settings']);
  return resolveMenu(raw, available);
}

let queue: Promise<void> = Promise.resolve();

/**
 * Change the menu. Each change starts from the stored menu (not from what is on
 * screen, which may lag behind a quick series of clicks) and runs after the previous one.
 * `fn` returns the new menu, or null for the default.
 */
export function useUpdateSidebarMenu(available: string[]) {
  const store = useStore();
  return (fn: (menu: MenuEntry[]) => MenuEntry[] | null) => {
    queue = queue
      .then(async () => {
        const next = fn(resolveMenu(await store.getSetting(SIDEBAR_MENU), available));
        await store.setSetting(SIDEBAR_MENU, next ? JSON.stringify(next) : '');
      })
      .catch((e: unknown) => console.error('sidebar menu', e));
    return queue;
  };
}

/** Move the item `id` to position `to` (clamped). */
export function moveEntry(menu: MenuEntry[], id: string, to: number): MenuEntry[] {
  const from = menu.findIndex((e) => e.id === id);
  if (from < 0) return menu;
  const next = [...menu];
  const [entry] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(next.length, to)), 0, entry!);
  return next;
}

/** Move the item `id` up (-1) or down (+1). */
export const moveEntryBy = (menu: MenuEntry[], id: string, delta: number) =>
  moveEntry(menu, id, menu.findIndex((e) => e.id === id) + delta);

/** Move the item `id` to where `target` is. */
export const moveEntryTo = (menu: MenuEntry[], id: string, target: string) =>
  moveEntry(
    menu,
    id,
    menu.findIndex((e) => e.id === target),
  );

export const setHidden = (menu: MenuEntry[], id: string, hidden: boolean) =>
  menu.map((e) => (e.id === id ? { ...e, hidden } : e));

/** Whether the "customise menu" dialog is open (from the sidebar or the settings). */
export const useMenuEditor = create<{ open: boolean; setOpen: (open: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));
